import React from 'react';
import HowItWorks from '../components/HowItWorks';
import { 
  Users, 
  ChatCircle, 
  Briefcase, 
  CheckCircle,
  Question,
  CaretDown,
  ArrowRight,
  HardHat,
  House,
  Camera,
  Microphone,
  Globe,
  Wallet,
  DownloadSimple,
  Sparkle,
  Translate,
  Wrench
} from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import DailyChecklistPromo from '../components/landing/DailyChecklistPromo';
import ShoppingListPromo from '../components/landing/ShoppingListPromo';

const ROLE_CONFIG = {
  homeowner: {
    label: 'For Homeowners',
    label_es: 'Para Propietarios',
    Icon: House,
    accent: '#2B4C6F',
    headline: "Here's exactly how you get your project done",
    headline_es: 'Así es exactamente cómo completas tu proyecto',
    subline: 'Free quotes, on-site visits, and milestone-based escrow — start to finish, all in one place.',
    subline_es: 'Cotizaciones gratis, visitas al sitio y depósito en garantía por etapas — de principio a fin, todo en un solo lugar.',
    cta: 'Get Free Quotes',
    cta_es: 'Recibir Cotizaciones Gratis',
    ctaRoute: '/get-quotes',
    scrollTo: 'homeowner-section',
  },
  contractor: {
    label: 'For Contractors',
    label_es: 'Para Contratistas',
    Icon: HardHat,
    accent: '#D95800',
    headline: 'Win more work, run your business smarter',
    headline_es: 'Gana más trabajos y administra tu negocio con inteligencia',
    subline: 'Verified leads, AI pricing tools, secure payouts, and a CRM that runs itself.',
    subline_es: 'Prospectos verificados, herramientas de precios con IA, pagos seguros y un CRM que se administra solo.',
    cta: 'Join as Contractor',
    cta_es: 'Únete como Contratista',
    ctaRoute: '/contractor-funnel',
    scrollTo: 'contractor-section',
  },
  worker: {
    label: 'For Workers',
    label_es: 'Para Trabajadores',
    Icon: Wrench,
    accent: '#10B981',
    headline: 'Find skilled work near you — every day',
    headline_es: 'Encuentra trabajo calificado cerca de ti — todos los días',
    subline: 'Auto-match with jobs, set your own rates, and get paid the moment work is done.',
    subline_es: 'Emparéjate automáticamente con trabajos, fija tus propias tarifas y cobra en cuanto terminas.',
    cta: 'Find Work',
    cta_es: 'Buscar Trabajo',
    ctaRoute: '/join-as-worker',
    scrollTo: 'worker-section',
  },
};

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);
  const role = ROLE_CONFIG[(searchParams.get('role') || '').toLowerCase()] || null;
  const [openFaq, setOpenFaq] = React.useState(null);

  // When entering with ?role=, smooth-scroll to that audience's section
  React.useEffect(() => {
    if (!role) return;
    const target = document.getElementById(role.scrollTo);
    if (!target) return;
    // Small delay so the page paint settles first
    const t = setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(t);
  }, [role]);

  const faqs = [
    {
      q: 'How do I get started?',
      q_es: '¿Cómo empiezo?',
      a: 'Sign up for free, complete your profile, and start browsing jobs or workers immediately. The whole onboarding takes about 3 minutes.',
      a_es: 'Regístrate gratis, completa tu perfil y empieza a explorar trabajos o trabajadores de inmediato. Todo el registro toma unos 3 minutos.'
    },
    {
      q: 'Is there a fee to join?',
      q_es: '¿Hay algún costo por unirse?',
      a: 'Joining ConstructHelp is completely free for basic features. Workers pay nothing — ever. Contractors only pay for premium leads they choose to claim or optional Pro/Premium subscription plans.',
      a_es: 'Unirse a ConstructHelp es completamente gratis para las funciones básicas. Los trabajadores no pagan nada — nunca. Los contratistas solo pagan por los prospectos premium que eligen reclamar o por los planes de suscripción Pro/Premium opcionales.'
    },
    {
      q: 'What is the AI Photo Estimator?',
      q_es: '¿Qué es el Estimador de Fotos con IA?',
      a: 'Contractors can upload up to 5 job-site photos and our GPT-5.2 Vision AI returns a scoped 3-tier estimate (Economy / Standard / Premium) with line-item materials and labor breakdown. Win more bids in minutes instead of hours.',
      a_es: 'Los contratistas pueden subir hasta 5 fotos del sitio de trabajo y nuestra IA de visión GPT-5.2 devuelve un estimado de 3 niveles (Económico / Estándar / Premium) con el desglose detallado de materiales y mano de obra. Gana más presupuestos en minutos en lugar de horas.'
    },
    {
      q: 'How does the Voice Translator work?',
      q_es: '¿Cómo funciona el Traductor de Voz?',
      a: 'Both contractors and workers get a voice translator supporting 12 languages (English, Spanish, Portuguese, French, Polish, Russian, Ukrainian, Mandarin, Vietnamese, Korean, Tagalog, Haitian Creole), powered by Whisper, GPT-5.2, and OpenAI TTS. Tap the mic, speak, and hear the translation instantly. Construction terms stay accurate (rebar ↔ varilla, drywall ↔ tablaroca, scaffold ↔ andamio).',
      a_es: 'Tanto los contratistas como los trabajadores obtienen un traductor de voz que admite 12 idiomas (inglés, español, portugués, francés, polaco, ruso, ucraniano, mandarín, vietnamita, coreano, tagalo, criollo haitiano), impulsado por Whisper, GPT-5.2 y OpenAI TTS. Toca el micrófono, habla y escucha la traducción al instante. Los términos de construcción se mantienen precisos (rebar ↔ varilla, drywall ↔ tablaroca, scaffold ↔ andamio).'
    },
    {
      q: 'Do I get a shareable profile?',
      q_es: '¿Obtengo un perfil que puedo compartir?',
      a: 'Yes — every contractor and worker gets a public profile at /pro/your-name with reviews, photo portfolio, and a Hire-Me CTA. The URL is rich-preview-ready for iMessage, Slack, LinkedIn, X, WhatsApp, and Facebook.',
      a_es: 'Sí — cada contratista y trabajador obtiene un perfil público en /pro/tu-nombre con reseñas, portafolio de fotos y un botón de "Contrátame". La URL está lista para vistas previas enriquecidas en iMessage, Slack, LinkedIn, X, WhatsApp y Facebook.'
    },
    {
      q: 'How does payment work?',
      q_es: '¿Cómo funcionan los pagos?',
      a: 'Homeowners and contractors fund escrow before work begins. As milestones are approved, money releases to your wallet. You can withdraw directly to your bank account via Stripe Connect — no platform fees.',
      a_es: 'Los propietarios y contratistas financian el depósito en garantía antes de que comience el trabajo. A medida que se aprueban las etapas, el dinero se libera a tu billetera. Puedes retirarlo directamente a tu cuenta bancaria a través de Stripe Connect — sin comisiones de plataforma.'
    },
    {
      q: 'Can I install ConstructHelp as an app?',
      q_es: '¿Puedo instalar ConstructHelp como una app?',
      a: 'Yes — ConstructHelp is a Progressive Web App. On Android/Chrome you\'ll see an "Install" prompt; on iPhone use Safari\'s Share → Add to Home Screen. Get faster load times, full-screen mode, push notifications, and app shortcuts (long-press the icon).',
      a_es: 'Sí — ConstructHelp es una Aplicación Web Progresiva. En Android/Chrome verás un aviso de "Instalar"; en iPhone usa Compartir de Safari → Agregar a la pantalla de inicio. Obtén cargas más rápidas, modo pantalla completa, notificaciones push y accesos directos (mantén presionado el ícono).'
    },
    {
      q: 'Are workers verified?',
      q_es: '¿Están verificados los trabajadores?',
      a: 'Workers go through profile verification. Contractors with license + insurance on file earn a "Verified Pro" badge that gets them 2× more lead views and is shown on their public profile.',
      a_es: 'Los trabajadores pasan por una verificación de perfil. Los contratistas con licencia + seguro en archivo obtienen una insignia de "Pro Verificado" que les da 2× más vistas de prospectos y se muestra en su perfil público.'
    },
    {
      q: 'What if there is a dispute?',
      q_es: '¿Qué pasa si hay una disputa?',
      a: 'We offer professional mediation services. Escrow funds remain held until both sides agree or our mediator resolves the dispute.',
      a_es: 'Ofrecemos servicios de mediación profesional. Los fondos en garantía permanecen retenidos hasta que ambas partes lleguen a un acuerdo o nuestro mediador resuelva la disputa.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]">
      {/* Role-specific hero banner — only shown when ?role= matches */}
      {role && (() => {
        const RoleIcon = role.Icon;
        return (
          <div
            className="border-b-4 border-black"
            style={{ backgroundColor: role.accent }}
            data-testid={`role-hero-${searchParams.get('role')}`}
          >
            <div className="max-w-5xl mx-auto px-4 py-10 flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-white border-4 border-black flex items-center justify-center flex-shrink-0">
                <RoleIcon size={44} weight="bold" style={{ color: role.accent }} />
              </div>
              <div className="text-center md:text-left flex-1">
                <span className="inline-block text-[10px] font-black uppercase tracking-widest bg-black text-white px-2 py-0.5 border-2 border-black mb-2">
                  {L(role.label, role.label_es)}
                </span>
                <h1 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight">
                  {L(role.headline, role.headline_es)}
                </h1>
                <p className="text-white/90 mt-2 max-w-2xl text-sm md:text-base">
                  {L(role.subline, role.subline_es)}
                </p>
              </div>
              <Button
                onClick={() => navigate(role.ctaRoute)}
                className="h-12 px-6 bg-white text-neutral-900 font-black uppercase border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] active:shadow-none active:translate-y-0.5 rounded-none transition-all flex-shrink-0"
                data-testid={`role-hero-cta-${searchParams.get('role')}`}
              >
                {L(role.cta, role.cta_es)}
                <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-black py-12">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
            {L('How It Works', 'Cómo Funciona')}
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto">
            {L('Smart tools, secure payments, and shareable profiles — built for contractors, workers, and homeowners', 'Herramientas inteligentes, pagos seguros y perfiles para compartir — creado para contratistas, trabajadores y propietarios')}
          </p>
        </div>
      </div>

      {/* Smart Tools Built In — NEW */}
      <section className="py-12 border-b-4 border-black bg-gradient-to-br from-neutral-100 to-white dark:from-neutral-900 dark:to-[#0F172A]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#D95800] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-black rounded mb-3">
              <Sparkle size={12} weight="fill" /> {L('Built-In AI Tools', 'Herramientas de IA Integradas')}
            </div>
            <h2 className="text-2xl md:text-3xl font-black uppercase">{L('Smart Tools, Free for Members', 'Herramientas Inteligentes, Gratis para Miembros')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-testid="smart-tools-grid">
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black p-5 shadow-[4px_4px_0px_#D95800]">
              <div className="w-12 h-12 bg-[#D95800] rounded-lg flex items-center justify-center border-2 border-black mb-3">
                <Camera size={24} weight="fill" className="text-white" />
              </div>
              <h3 className="font-black text-base uppercase mb-1">{L('AI Photo Estimator', 'Estimador de Fotos con IA')}</h3>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{L('Snap up to 5 site photos → GPT-5.2 returns a 3-tier estimate with materials & labor. Bid in minutes.', 'Toma hasta 5 fotos del sitio → GPT-5.2 devuelve un estimado de 3 niveles con materiales y mano de obra. Presupuesta en minutos.')}</p>
              <span className="inline-block mt-3 text-[9px] font-black uppercase tracking-widest bg-[#D95800] text-white px-2 py-0.5 rounded">{L('For Contractors', 'Para Contratistas')}</span>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black p-5 shadow-[4px_4px_0px_#10b981]">
              <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center border-2 border-black mb-3">
                <Microphone size={24} weight="fill" className="text-white" />
              </div>
              <h3 className="font-black text-base uppercase mb-1">{L('Voice Translator', 'Traductor de Voz')}</h3>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{L('12 languages. Tap, speak, hear. Construction terms stay accurate (rebar ↔ varilla).', '12 idiomas. Toca, habla, escucha. Los términos de construcción se mantienen precisos (rebar ↔ varilla).')}</p>
              <span className="inline-block mt-3 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-white px-2 py-0.5 rounded">{L('Contractors + Workers', 'Contratistas + Trabajadores')}</span>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black p-5 shadow-[4px_4px_0px_#2B4C6F]">
              <div className="w-12 h-12 bg-[#2B4C6F] rounded-lg flex items-center justify-center border-2 border-black mb-3">
                <Globe size={24} weight="fill" className="text-white" />
              </div>
              <h3 className="font-black text-base uppercase mb-1">{L('Public Profile', 'Perfil Público')}</h3>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{L('Shareable URL with reviews, portfolio & Hire-Me CTA. Rich previews on iMessage, Slack, X, FB.', 'URL para compartir con reseñas, portafolio y botón de Contrátame. Vistas previas enriquecidas en iMessage, Slack, X, FB.')}</p>
              <span className="inline-block mt-3 text-[9px] font-black uppercase tracking-widest bg-[#2B4C6F] text-white px-2 py-0.5 rounded">{L('Contractors + Workers', 'Contratistas + Trabajadores')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-12 border-b-4 border-black bg-white dark:bg-[#0F172A]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Users, title: L('Connect', 'Conecta'), desc: L('Find the right match for your needs', 'Encuentra la coincidencia ideal para ti'), color: 'bg-[#2B4C6F]' },
              { icon: Translate, title: L('Communicate', 'Comunícate'), desc: L('Voice translator for any language', 'Traductor de voz para cualquier idioma'), color: 'bg-emerald-500' },
              { icon: Briefcase, title: L('Collaborate', 'Colabora'), desc: L('Manage projects with smart AI tools', 'Administra proyectos con herramientas de IA'), color: 'bg-[#D95800]' },
              { icon: Wallet, title: L('Get Paid', 'Cobra'), desc: L('Escrow + direct bank payouts', 'Depósito en garantía + pagos directos al banco'), color: 'bg-amber-500' },
            ].map((item, index) => (
              <div key={index} className="text-center p-4 border-2 border-black bg-neutral-50 dark:bg-neutral-800">
                <div className={`w-14 h-14 ${item.color} border-2 border-black flex items-center justify-center mx-auto mb-3`}>
                  <item.icon size={28} weight="bold" className="text-white" />
                </div>
                <h3 className="font-black uppercase text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - For Contractors & Workers */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Contractors */}
            <div id="contractor-section" className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#D95800] scroll-mt-24">
              <div className="bg-[#D95800] p-4 border-b-4 border-black">
                <div className="flex items-center gap-3">
                  <HardHat size={32} weight="bold" className="text-white" />
                  <h2 className="text-xl font-black text-white uppercase">{L('For Contractors', 'Para Contratistas')}</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { step: 1, title: L('Post Jobs or Buy Leads', 'Publica Trabajos o Compra Prospectos'), desc: L('Post jobs free OR browse the Leads Marketplace — pay only when you claim.', 'Publica trabajos gratis O explora el Mercado de Prospectos — paga solo cuando reclamas.') },
                  { step: 2, title: L('Estimate with AI Vision', 'Estima con IA de Visión'), desc: L('Snap site photos and get a 3-tier scoped estimate in seconds.', 'Toma fotos del sitio y obtén un estimado de 3 niveles en segundos.') },
                  { step: 3, title: L('Hire & Communicate', 'Contrata y Comunícate'), desc: L('Review applicants, message in-app, translate any language with the voice translator.', 'Revisa postulantes, envía mensajes en la app y traduce cualquier idioma con el traductor de voz.') },
                  { step: 4, title: L('Get Paid to Your Bank', 'Cobra en tu Banco'), desc: L('Escrow protects every milestone. Withdraw to your bank via Stripe Connect — no fees.', 'El depósito en garantía protege cada etapa. Retira a tu banco vía Stripe Connect — sin comisiones.') },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-10 h-10 bg-[#D95800] text-white font-black flex items-center justify-center border-2 border-black flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-bold">{item.title}</p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
                <Button 
                  onClick={() => navigate('/signup?type=contractor')}
                  className="w-full h-12 bg-[#D95800] hover:bg-[#E55D00] text-white font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none mt-4"
                >
                  {L('Get Started as Contractor', 'Empieza como Contratista')}
                  <ArrowRight size={20} weight="bold" className="ml-2" />
                </Button>
              </div>
            </div>

            {/* For Workers */}
            <div id="worker-section" className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#2B4C6F] scroll-mt-24">
              <div className="bg-[#2B4C6F] p-4 border-b-4 border-black">
                <div className="flex items-center gap-3">
                  <Users size={32} weight="bold" className="text-white" />
                  <h2 className="text-xl font-black text-white uppercase">{L('For Workers', 'Para Trabajadores')}</h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {[
                  { step: 1, title: L('Create Your Profile', 'Crea tu Perfil'), desc: L('Showcase skills, certifications, photos. Auto-generated shareable URL.', 'Muestra habilidades, certificaciones y fotos. URL para compartir generada automáticamente.') },
                  { step: 2, title: L('Find Jobs in Your Language', 'Encuentra Trabajos en tu Idioma'), desc: L('Browse jobs by trade/area. Onboarding & translator available in Español.', 'Explora trabajos por oficio/área. Registro y traductor disponibles en Español.') },
                  { step: 3, title: L('Apply & Talk to Any Crew', 'Postula y Habla con Cualquier Equipo'), desc: L('Personalized applications + EN↔ES voice translator on every conversation.', 'Postulaciones personalizadas + traductor de voz EN↔ES en cada conversación.') },
                  { step: 4, title: L('Get Paid Direct to Bank', 'Cobra Directo al Banco'), desc: L('Escrow guarantees payment. Withdraw to your bank via Stripe Connect.', 'El depósito en garantía garantiza el pago. Retira a tu banco vía Stripe Connect.') },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-10 h-10 bg-[#2B4C6F] text-white font-black flex items-center justify-center border-2 border-black flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-bold">{item.title}</p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
                <Button 
                  onClick={() => navigate('/signup?type=worker')}
                  className="w-full h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none mt-4"
                >
                  {L('Get Started as Worker', 'Empieza como Trabajador')}
                  <ArrowRight size={20} weight="bold" className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Checklist promo — role-aware */}
      {(searchParams.get('role') === 'contractor' || searchParams.get('role') === 'worker') && (
        <DailyChecklistPromo role={searchParams.get('role')} />
      )}

      {(searchParams.get('role') === 'contractor' || searchParams.get('role') === 'worker' || !searchParams.get('role')) && (
        <ShoppingListPromo role={searchParams.get('role') || 'contractor'} />
      )}

      {/* Install as App callout */}
      <section className="py-10 bg-gradient-to-br from-purple-900 to-indigo-900 border-y-4 border-black">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 bg-purple-500 border-4 border-white flex items-center justify-center flex-shrink-0">
              <DownloadSimple size={48} weight="bold" className="text-white" />
            </div>
            <div className="text-center md:text-left flex-1">
              <span className="inline-block text-[10px] font-black uppercase tracking-widest bg-white text-purple-700 px-2 py-0.5 border border-black rounded mb-2">{L('PWA · Installable', 'PWA · Instalable')}</span>
              <h2 className="text-2xl font-black text-white uppercase mb-2">{L('Install on Your Phone', 'Instálala en tu Teléfono')}</h2>
              <p className="text-neutral-300">
                {L('Tap-from-home-screen access. Push notifications for new leads. Faster cold-starts. App shortcuts for AI Pricing, Translator, and Leads — built for life on the job site.', 'Acceso desde la pantalla de inicio. Notificaciones push de nuevos prospectos. Arranques más rápidos. Accesos directos a Precios con IA, Traductor y Prospectos — hecho para la vida en la obra.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Homeowners */}
      <section id="homeowner-section" className="py-12 bg-[#0F172A] border-b-4 border-black scroll-mt-24">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 bg-emerald-500 border-4 border-white flex items-center justify-center flex-shrink-0">
              <House size={48} weight="bold" className="text-white" />
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="text-2xl font-black text-white uppercase mb-2">{L('For Homeowners', 'Para Propietarios')}</h2>
              <p className="text-neutral-400 mb-4">
                {L("Need work done on your home? Get free quotes from local contractors — verified badges show who's ID-checked.", '¿Necesitas trabajo en tu hogar? Recibe cotizaciones gratis de contratistas locales — las insignias de verificación muestran quién tiene identidad verificada.')}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/get-quotes')}
              className="h-12 px-8 bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase border-2 border-white shadow-[4px_4px_0px_#000] rounded-none"
            >
              {L('Get Free Quotes', 'Recibir Cotizaciones Gratis')}
              <ArrowRight size={20} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Question size={36} weight="bold" className="text-[#2B4C6F]" />
            <h2 className="text-3xl font-black uppercase">FAQ</h2>
          </div>
          
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white dark:bg-[#0F172A] border-2 border-black"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-4 flex items-center justify-between font-bold text-left hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                >
                  <span>{L(faq.q, faq.q_es)}</span>
                  <CaretDown 
                    size={20} 
                    weight="bold" 
                    className={`transform transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 pt-0 text-neutral-600 dark:text-neutral-400 border-t border-neutral-200 dark:border-neutral-700">
                    {L(faq.a, faq.a_es)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#2B4C6F] to-[#D95800] border-t-4 border-black">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-4">
            {L('Ready to Get Started?', '¿Listo para Empezar?')}
          </h2>
          <p className="text-white/80 mb-6">
            {L('Join ConstructHelp today and start building your future.', 'Únete a ConstructHelp hoy y empieza a construir tu futuro.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/signup')}
              className="h-12 px-8 bg-white text-[#2B4C6F] font-bold uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none hover:bg-neutral-100"
            >
              {L('Create Account', 'Crear Cuenta')}
            </Button>
            <Button 
              onClick={() => navigate('/home')}
              variant="outline"
              className="h-12 px-8 bg-transparent text-white font-bold uppercase border-2 border-white rounded-none hover:bg-white/10"
            >
              {L('Learn More', 'Más Información')}
            </Button>
          </div>
        </div>
      </section>

      {/* Sticky bottom CTA — appears only on role-aware HowItWorks pages */}
      {role && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-black border-t-4 border-[#D95800] shadow-[0_-6px_0_0_rgba(0,0,0,1)]"
          data-testid="how-it-works-sticky-cta"
        >
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex items-center gap-3">
              <div
                className="w-10 h-10 border-2 border-white flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: role.accent }}
              >
                <role.Icon size={20} weight="bold" className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-neutral-400 leading-none">
                  {L(role.label, role.label_es)}
                </div>
                <div className="text-sm font-black text-white uppercase truncate">
                  {L(role.cta, role.cta_es)}
                </div>
              </div>
            </div>
            <Button
              onClick={() => navigate(role.ctaRoute)}
              className="h-11 px-4 md:px-6 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase tracking-wider text-sm border-2 border-white shadow-[3px_3px_0px_#fff] hover:shadow-[4px_4px_0px_#fff] active:translate-y-0.5 active:shadow-none rounded-none transition-all flex-shrink-0"
              data-testid="how-it-works-sticky-cta-button"
            >
              <span className="hidden sm:inline">{L('Get Started', 'Empezar')}</span>
              <ArrowRight size={18} weight="bold" className="ml-1 sm:ml-2" />
            </Button>
          </div>
        </div>
      )}
      {role && <div aria-hidden className="h-20" />}
    </div>
  );
};

export default HowItWorksPage;
